import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Divider,
  Tooltip, IconButton, Stack, Table, TableBody, TableCell, TableRow
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import SearchIcon from '@mui/icons-material/Search';
import ScienceIcon from '@mui/icons-material/Science';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { authGet, get } from '../../services/apiService';

const DOMAIN_GREEN = '#1D4434';
const BUGS_COLOR   = '#b71c1c';
const PI_COLOR     = '#0d47a1';
const SYNTH_COLOR  = '#4a148c';

const fmtRelative = iso => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 2)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const BUG_CLOSED_STATES = new Set(['closed', 'resolved', 'done', 'completed', 'removed', 'wont fix', 'cancelled', 'duplicate']);
const isBugClosed = s => BUG_CLOSED_STATES.has((s || '').toLowerCase().trim());

// ─── MetricTile ───────────────────────────────────────────────────────────────

function MetricTile({ label, value, color = DOMAIN_GREEN, loading }) {
  return (
    <Box sx={{ textAlign: 'center', px: 0.5 }}>
      {loading
        ? <Skeleton variant="text" width={44} height={38} sx={{ mx: 'auto' }} />
        : <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1 }}>
            {value ?? '—'}
          </Typography>
      }
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── DomainCard ───────────────────────────────────────────────────────────────

function DomainCard({ title, icon, accentColor, onNavigate, children, error }) {
  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, py: 1.5,
        background: `linear-gradient(135deg, ${accentColor}ee 0%, ${accentColor}bb 100%)`,
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color: '#fff', display: 'flex', alignItems: 'center' }}>{icon}</Box>
          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3 }}>
            {title}
          </Typography>
        </Stack>
        {onNavigate && (
          <Tooltip title={`Open ${title}`}>
            <IconButton size="small" onClick={onNavigate}
              sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } }}>
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 2.5, flexGrow: 1 }}>
        {error
          ? <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              Unable to load data
            </Typography>
          : children
        }
      </Box>
    </Paper>
  );
}

// ─── OpenIssueBreakdown ───────────────────────────────────────────────────────

const isPiOpen = s => !['closed', 'resolved', 'done', 'completed'].includes((s || '').toLowerCase().trim());

function OpenIssueBreakdown({ byState, newToday, closedToday, loading, accentColor, closedCheck }) {
  const openStates = (byState || []).filter(s => closedCheck ? !closedCheck(s.state ?? s._id) : isPiOpen(s.state ?? s._id));
  const stateKey = s => s.state ?? s._id;
  const sorted = [...openStates].sort((a, b) => b.count - a.count);
  const totalOpen = sorted.reduce((sum, s) => sum + s.count, 0);

  return (
    <Box sx={{ mt: 0 }}>
      <Divider sx={{ my: 1.5 }} />

      {/* Today row */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5,
          bgcolor: 'rgba(56,142,60,0.08)', borderRadius: 1, px: 1, py: 0.5 }}>
          <AddCircleOutlineIcon sx={{ fontSize: 13, color: '#388e3c' }} />
          {loading
            ? <Skeleton variant="text" width={22} height={16} />
            : <Typography variant="caption" sx={{ fontWeight: 700, color: '#388e3c', fontSize: '0.72rem' }}>
                {newToday ?? '—'} new today
              </Typography>}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5,
          bgcolor: 'rgba(2,136,209,0.08)', borderRadius: 1, px: 1, py: 0.5 }}>
          <CheckCircleIcon sx={{ fontSize: 13, color: '#0288d1' }} />
          {loading
            ? <Skeleton variant="text" width={22} height={16} />
            : <Typography variant="caption" sx={{ fontWeight: 700, color: '#0288d1', fontSize: '0.72rem' }}>
                {closedToday ?? '—'} closed today
              </Typography>}
        </Box>
      </Stack>

      {/* State breakdown table */}
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', fontSize: '0.63rem', display: 'block', mb: 0.5 }}>
        Open by State
      </Typography>
      {loading
        ? <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
        : sorted.length === 0
          ? <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>No open issues</Typography>
          : <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Table size="small" padding="none">
                <TableBody>
                  {sorted.map(s => (
                    <TableRow key={stateKey(s)} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ px: 1, py: 0.4, borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.primary' }}>
                          {stateKey(s)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ px: 1, py: 0.4, borderColor: 'divider', minWidth: 32 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', color: accentColor }}>
                          {s.count}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ px: 1, py: 0.4 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Open Total</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ px: 1, py: 0.4, minWidth: 32 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', color: accentColor }}>
                        {totalOpen}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
      }
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const ServiceOperationsOverviewTab = ({ onNavigate }) => {
  const [piData,   setPiData]   = useState(null);
  const [piLoading, setPiLoad]  = useState(true);
  const [piError,  setPiError]  = useState(false);

  const [bugsData,  setBugsData]  = useState(null);
  const [bugsLoading, setBugsLoad] = useState(true);
  const [bugsError, setBugsError] = useState(false);

  const [hcRun,    setHcRun]    = useState(null);
  const [hcLoading, setHcLoad]  = useState(true);
  const [hcError,  setHcError]  = useState(false);

  const [synthRun,  setSynthRun]  = useState(null);
  const [synthLoading, setSynthLoad] = useState(true);
  const [synthError, setSynthError] = useState(false);

  const [ts, setTs] = useState(0);

  const loadPI = useCallback(async () => {
    setPiLoad(true); setPiError(false);
    try {
      const res = await authGet('/api/platform-investigation/summary');
      if (res?.success) setPiData(res.data);
      else setPiError(true);
    } catch { setPiError(true); }
    finally { setPiLoad(false); }
  }, []);

  const loadBugs = useCallback(async () => {
    setBugsLoad(true); setBugsError(false);
    try {
      const res = await get('/api/workitems/production-bugs/public/stats');
      if (res?.success) setBugsData(res.data);
      else setBugsError(true);
    } catch { setBugsError(true); }
    finally { setBugsLoad(false); }
  }, []);

  const loadHC = useCallback(async () => {
    setHcLoad(true); setHcError(false);
    try {
      const res = await authGet('/api/health-check/runs', { limit: 1 });
      if (res?.success) setHcRun((res.runs || [])[0] || null);
      else setHcError(true);
    } catch { setHcError(true); }
    finally { setHcLoad(false); }
  }, []);

  const loadSynth = useCallback(async () => {
    setSynthLoad(true); setSynthError(false);
    try {
      const res = await authGet('/api/synthetic-tests/runs/latest');
      if (res?.success) setSynthRun(res.data || null);
      else setSynthError(true);
    } catch { setSynthError(true); }
    finally { setSynthLoad(false); }
  }, []);

  useEffect(() => {
    loadPI();
    loadBugs();
    loadHC();
    loadSynth();
  }, [loadPI, loadBugs, loadHC, loadSynth, ts]);

  const refresh = () => setTs(t => t + 1);

  // ── PI derived ────────────────────────────────────────────────────────────
  const piOverview     = piData?.overview;
  const piOpen         = piOverview?.open ?? null;
  const piTotal        = piOverview?.total ?? null;
  const piPriority     = piOverview?.byPriority || [];
  const piP1           = piPriority.find(p => p._id === 1)?.count ?? 0;
  const piP2           = piPriority.find(p => p._id === 2)?.count ?? 0;
  const piTrends       = piData?.trends || [];
  const now            = new Date();
  const thisMonthKey   = now.getFullYear() * 100 + (now.getMonth() + 1);
  const piThisMonth    = piTrends.find(t => t.key === thisMonthKey);
  const piNewThisMonth = piThisMonth?.created ?? null;
  const piByState      = piOverview?.byState || [];
  const piNewToday     = piOverview?.newToday ?? null;
  const piClosedToday  = piOverview?.closedToday ?? null;

  // ── Bugs derived ──────────────────────────────────────────────────────────
  const bugsBreakdown  = bugsData?.byState || [];
  const bugsTotal      = bugsData?.total ?? null;
  const bugsOpen       = bugsTotal != null
    ? bugsBreakdown.filter(s => !isBugClosed(s.state)).reduce((sum, s) => sum + s.count, 0)
    : null;
  const bugsClosed     = bugsTotal != null ? bugsTotal - bugsOpen : null;
  const bugsAvgPerWeek = bugsData?.metrics?.avgIncomingPerWeek != null
    ? Math.round(bugsData.metrics.avgIncomingPerWeek * 10) / 10
    : null;
  const bugsNewToday   = bugsData?.metrics?.newToday ?? null;
  const bugsClosedToday = bugsData?.metrics?.closedToday ?? null;

  // ── Health Check derived ──────────────────────────────────────────────────
  const hcHealthy   = hcRun?.healthy   ?? null;
  const hcUnhealthy = hcRun?.unhealthy ?? null;
  const hcErrors    = hcRun?.errors    ?? null;
  const hcTotal     = hcRun?.total     ?? null;
  const hcHealthPct = hcTotal ? Math.round(((hcHealthy || 0) / hcTotal) * 100) : null;

  // ── Synthetic derived ─────────────────────────────────────────────────────
  const synthSummary    = synthRun?.summary;
  const synthTotal      = synthSummary?.total ?? null;
  const synthSuccess    = synthSummary?.success ?? null;
  const synthFailed     = synthSummary?.error ?? null;
  const synthSuccessPct = synthTotal ? Math.round((synthSuccess / synthTotal) * 100) : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: DOMAIN_GREEN }}>
            Service Operations Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live snapshot across all workstreams
          </Typography>
        </Box>
        <Tooltip title="Refresh all">
          <IconButton onClick={refresh} size="small" disabled={piLoading && bugsLoading && hcLoading && synthLoading}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3}>

        {/* ── Platform Investigation ───────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <DomainCard
            title="Platform Investigation"
            icon={<SearchIcon />}
            accentColor={PI_COLOR}
            onNavigate={onNavigate ? () => onNavigate('platform-investigation') : null}
            error={piError}
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <MetricTile label="Open" value={piOpen} loading={piLoading} color={PI_COLOR} />
              </Grid>
              <Grid item xs={6}>
                <MetricTile label="Total" value={piTotal} loading={piLoading} color="#555" />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={4}>
                <MetricTile label="P1 Critical" value={piLoading ? null : piP1} loading={piLoading}
                  color={piP1 > 0 ? '#c62828' : '#388e3c'} />
              </Grid>
              <Grid item xs={4}>
                <MetricTile label="P2 High" value={piLoading ? null : piP2} loading={piLoading}
                  color={piP2 > 0 ? '#e65100' : '#388e3c'} />
              </Grid>
              <Grid item xs={4}>
                <MetricTile label="New this month" value={piLoading ? null : piNewThisMonth} loading={piLoading} color="#555" />
              </Grid>
            </Grid>

            {!piLoading && !piError && piP1 === 0 && piP2 === 0 && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 14 }} />
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                  No P1 or P2 tickets open
                </Typography>
              </Box>
            )}

            <OpenIssueBreakdown
              byState={piByState}
              newToday={piNewToday}
              closedToday={piClosedToday}
              loading={piLoading}
              accentColor={PI_COLOR}
            />
          </DomainCard>
        </Grid>

        {/* ── Production Bugs ───────────────────────────────────────────── */}
        <Grid item xs={12} md={4}>
          <DomainCard
            title="Production Bugs"
            icon={<BugReportIcon />}
            accentColor={BUGS_COLOR}
            onNavigate={onNavigate ? () => onNavigate('production-bugs') : null}
            error={bugsError}
          >
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <MetricTile label="Open" value={bugsOpen} loading={bugsLoading} color={BUGS_COLOR} />
              </Grid>
              <Grid item xs={6}>
                <MetricTile label="Total" value={bugsTotal} loading={bugsLoading} color="#555" />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <MetricTile label="Closed" value={bugsClosed} loading={bugsLoading} color="#388e3c" />
              </Grid>
              <Grid item xs={6}>
                <MetricTile label="Avg/week (3mo)" value={bugsAvgPerWeek} loading={bugsLoading} color="#555" />
              </Grid>
            </Grid>

            {!bugsLoading && !bugsError && bugsOpen === 0 && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 14 }} />
                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                  No open production bugs
                </Typography>
              </Box>
            )}

            <OpenIssueBreakdown
              byState={bugsBreakdown}
              newToday={bugsNewToday}
              closedToday={bugsClosedToday}
              loading={bugsLoading}
              accentColor={BUGS_COLOR}
              closedCheck={isBugClosed}
            />
          </DomainCard>
        </Grid>

        {/* ── Synthetic Tests + Health Check ───────────────────────────── */}
        <Grid item xs={12} md={4}>
          <DomainCard
            title="Synthetic Tests & Health Check"
            icon={<ScienceIcon />}
            accentColor={SYNTH_COLOR}
            onNavigate={onNavigate ? () => onNavigate('synthetic-tests') : null}
            error={synthError && hcError}
          >
            {/* Synthetic Tests */}
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
                <ScienceIcon sx={{ fontSize: 13, color: SYNTH_COLOR }} />
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: SYNTH_COLOR }}>
                  Synthetic Tests
                </Typography>
                {!synthLoading && synthRun && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.68rem' }}>
                    {fmtRelative(synthRun.completedAt || synthRun.createdAt)}
                  </Typography>
                )}
              </Stack>

              {synthError
                ? <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Unable to load</Typography>
                : <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <MetricTile label="Total" value={synthTotal} loading={synthLoading} color={SYNTH_COLOR} />
                    </Grid>
                    <Grid item xs={4}>
                      <MetricTile label="Passed" value={synthSuccess} loading={synthLoading} color="#388e3c" />
                    </Grid>
                    <Grid item xs={4}>
                      <MetricTile
                        label="Pass %"
                        value={synthLoading ? null : synthSuccessPct != null ? `${synthSuccessPct}%` : '—'}
                        loading={synthLoading}
                        color={synthSuccessPct != null ? (synthSuccessPct >= 90 ? '#388e3c' : synthSuccessPct >= 70 ? '#e65100' : '#c62828') : '#555'}
                      />
                    </Grid>
                  </Grid>
              }

              {!synthLoading && !synthError && synthFailed > 0 && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 13 }} />
                  <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                    {synthFailed} test{synthFailed > 1 ? 's' : ''} failed
                  </Typography>
                </Box>
              )}
              {!synthLoading && !synthError && synthRun == null && (
                <Typography variant="caption" color="text.disabled">No synthetic runs yet</Typography>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Health Check */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
                <MonitorHeartIcon sx={{ fontSize: 13, color: SYNTH_COLOR }} />
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: SYNTH_COLOR }}>
                  Health Check
                </Typography>
                {!hcLoading && hcRun && (
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.68rem' }}>
                    {fmtRelative(hcRun.completedAt || hcRun.createdAt)}
                  </Typography>
                )}
              </Stack>

              {hcError
                ? <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Unable to load</Typography>
                : <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <MetricTile label="Total"   value={hcTotal}   loading={hcLoading} color={SYNTH_COLOR} />
                    </Grid>
                    <Grid item xs={3}>
                      <MetricTile label="Healthy" value={hcHealthy} loading={hcLoading} color="#388e3c" />
                    </Grid>
                    <Grid item xs={3}>
                      <MetricTile
                        label="Issues"
                        value={hcLoading ? null : (hcUnhealthy ?? 0) + (hcErrors ?? 0)}
                        loading={hcLoading}
                        color={(hcUnhealthy || 0) + (hcErrors || 0) > 0 ? '#c62828' : '#388e3c'}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <MetricTile
                        label="Health %"
                        value={hcLoading ? null : hcHealthPct != null ? `${hcHealthPct}%` : '—'}
                        loading={hcLoading}
                        color={hcHealthPct != null ? (hcHealthPct >= 90 ? '#388e3c' : hcHealthPct >= 70 ? '#e65100' : '#c62828') : '#555'}
                      />
                    </Grid>
                  </Grid>
              }

              {!hcLoading && !hcError && hcRun == null && (
                <Typography variant="caption" color="text.disabled">No health check runs yet</Typography>
              )}
            </Box>
          </DomainCard>
        </Grid>

      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Typography variant="caption" color="text.disabled">
          Last refreshed: {new Date().toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default ServiceOperationsOverviewTab;
